/**
 * @prettier
 */

import _fromPairs from 'lodash/fromPairs';
import React, { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import { ButtonV2 } from '@zola/zola-ui';
import PhotoUploadIcon from 'assets/images/invitations/single-sample-modal/photo-upload-icon.svg';
import UploadcareWidget from 'components/common/UploadcareWidget/UploadcareWidget';
import { isUploadcareEnabled } from 'components/common/UploadcareWidget/utils';
import { ptToPx } from '../../../../util';
import { usePhotoObjectURL, usePhotoUploadHandler } from '../util/hooks';
import { templateHasCustomImage } from '../util/helpers';

import styles from './styles.module.less';

const UploadButton = ({ disabled, editable, invalid, onClick }) => {
  const buttonText =
    (disabled && 'Loading...') ||
    (editable && 'Edit Photo') ||
    (invalid && 'Try another file') ||
    'Upload a photo';

  return (
    <ButtonV2 disabled={disabled} onClick={onClick}>
      {buttonText}
    </ButtonV2>
  );
};

const FileUploader = ({ onSelect, renderButton }) => {
  const inputEl = useRef(null);

  const onClick = () => {
    inputEl.current.click();
  };

  const onSelectFile = () => {
    onSelect(inputEl.current.files[0]);
  };

  return (
    <Fragment>
      <input
        ref={inputEl}
        className={styles.hiddenInput}
        type="file"
        accept="image/jpeg,image/png"
        onChange={onSelectFile}
      />
      {renderButton(onClick)}
    </Fragment>
  );
};

const Dropzone = ({
  form,
  imageElement,
  updateForm,
  cardSuiteUUIDList,
  onSuccess,
  minDimensions,
  defaultTemplateSet,
}) => {
  const dropzoneIcon = <img alt="" src={PhotoUploadIcon} className={styles.dropzoneIcon} />;
  const busy = !(imageElement && imageElement.uuid);
  const { photo, uploadcareFile: previousUploadcareFile } = form.customizations;

  const onSelect = usePhotoUploadHandler(
    updateForm,
    minDimensions,
    false,
    null,
    cardSuiteUUIDList,
    defaultTemplateSet,
    onSuccess
  );

  const saveUploadcareImageToForm = useCallback(
    uploadcareFile => {
      const uploadcareFileSet =
        cardSuiteUUIDList &&
        _fromPairs(
          cardSuiteUUIDList
            .filter(uuid => templateHasCustomImage(defaultTemplateSet[uuid]))
            .map(uuid => [uuid, uploadcareFile])
        );

      updateForm({
        customizations: {
          uploadcareFile,
          uploadcareFileSet,
        },
      });

      onSuccess();
    },
    [defaultTemplateSet, updateForm, cardSuiteUUIDList, onSuccess]
  );

  const cropRatio = imageElement ? `${imageElement.width}:${imageElement.height}` : null;

  return (
    <div className={styles.dropzoneBox}>
      {dropzoneIcon}
      <div className={styles.dropzoneText}>
        Image must be at least {minDimensions.width} by {minDimensions.height} pixels and in JPG or
        PNG format.
        <br />
        (max 10MB)
      </div>
      <div>
        {isUploadcareEnabled ? (
          <UploadcareWidget
            cachedValue={previousUploadcareFile ? previousUploadcareFile.cdnUrl : null}
            cropRatio={cropRatio}
            minWidth={1200}
            minHeight={1200}
            onUploadcareFileReady={saveUploadcareImageToForm}
            renderButton={(inProgress, editable, onClick) => (
              <UploadButton
                disabled={inProgress}
                editable={editable}
                invalid={false}
                onClick={onClick}
              />
            )}
          />
        ) : (
          <FileUploader
            onSelect={onSelect}
            renderButton={onClick => (
              <UploadButton
                disabled={busy}
                editable={false}
                invalid={photo.file && !photo.valid}
                onClick={onClick}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

Dropzone.defaultProps = {
  minDimensions: { width: 1200, height: 1200 },
};

const Crop = ({ activePreview, form, page, updateForm, isMulti }) => {
  const { photo, photoSet } = form.customizations;

  const setPixelCrop = pixelCrop => {
    if (!isMulti) {
      updateForm({ customizations: { pixelCrop } });
    }

    if (activePreview) {
      updateForm({ customizations: { pixelCropSet: { [activePreview]: pixelCrop } } });
    }
  };

  const photoURL = usePhotoObjectURL(isMulti && activePreview ? photoSet[activePreview] : photo);

  const [crop, setCrop] = useState({ aspect: 1, width: 0, x: 0, y: 0 });

  useEffect(() => {
    const customImageElement =
      page && page.elements.find(e => e.content_type === 'IMAGE' && e.customizable);

    if (!customImageElement) {
      return;
    }

    const aspect = customImageElement.width / customImageElement.height;

    setCrop({ aspect, width: ptToPx(customImageElement.width), x: 0, y: 0 });
  }, [page.id]);

  const onChange = (inputCrop, inputPixelCrop) => {
    setCrop(inputCrop);
    setPixelCrop(inputPixelCrop);
  };

  if (!photoURL) {
    return null;
  }

  return (
    <ReactCrop
      className={styles.crop}
      src={photoURL}
      crop={crop}
      onComplete={onChange}
      onChange={onChange}
      keepSelection
    />
  );
};

export default { UploadButton, FileUploader, Dropzone, Crop };



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/PhotoUpload/PhotoUpload.jsx